import * as React from 'react';
import type { BannerType} from '@sunwebgroup/ui-components';
import {ServiceType} from "../../domain/types";
import {Button, SpaceBetween, Typography, tk, Banner, BulletList} from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import {useService} from '@/scopes/checkout/domain/hooks';
import ServiceBox from './ServiceBox';
import {styled, css} from 'styled-components';
import {LazyImage} from '@/scopes/common/components/Image';
import {ShowMore} from '../../../common/components/ShowMore';

import {updateBookingRedeemerService} from "../../domain/actions";
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable';
import { createAncillariesClickedEvent } from '../../domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon';
import { Icon } from '@sunwebgroup/ui-components/uds';

export function BookingRedeemerService() {
  const intl = useIntl();
  const { service, setServiceValue } = useService(ServiceType.BookingRedeem);
  const { sendEvent } = useAnalytics();
  useSendAncillaryAvailable(service, 'selectBookingRedeemer');

  if (!service) return null

  const { bookings = {}, selectedValue } = service

  if (!Object.keys(bookings).length) return null

  const handleClickValue =
  (bookingNumber: string | null) => {
    if (!service) return;
    setServiceValue(updateBookingRedeemerService({
      value: bookingNumber
    })(service));

    sendEvent(
      createAncillariesClickedEvent({
        result: bookingNumber ? 'booking selected' : 'booking removed',
        type: 'booking redeemer',
        description: 'selectBookingRedeemer',
        element: 'button',
      })
    );
  }

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_description' })}
      glyph={getIconForService(ServiceType.BookingRedeem)}
      modalTitle=""
      modalDescription=""
    >
      <BookingRedeemerUsps>
        <BulletList.Item $icon="IconCheckmark">
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_usp_1' })}
        </BulletList.Item>
        <BulletList.Item $icon="IconCheckmark">
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_usp_2' })}
        </BulletList.Item>
        <BulletList.Item $icon="IconCheckmark">
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_usp_3' })}
        </BulletList.Item>
      </BookingRedeemerUsps>
      <DescriptionTexts>
        { Object.keys(bookings).length > 0 && <Typography $variant="label">{intl.formatMessage({id: 'checkout_steps_ancillaries_booking_redeemer_title_on_top_of_bookings'})}</Typography> }
        { !Object.keys(bookings).length && <Typography $variant="body-sm">
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_text_when_no_bookings_available' })}
        </Typography>}
      </DescriptionTexts>

      <ShowMore
      openLabel={intl.formatMessage({id: 'show_more'})}
      closeLabel={intl.formatMessage({id: 'show_less'})}
      visibleItems={2}
      >
        { (Object.values(bookings) || []).map((booking) => (
          <BookingRedeem key={booking.bookingNumber} $active={selectedValue  === booking.bookingNumber}>
            <BookingRedeemerCard>
              <ImageWrapper>
                <LazyImage
                  src={booking.accoImageUrl}
                  alt={booking?.description}
                  srcSet={[560, 940, 1200]}
                  sizes="(min-width: 1280px) 936px, 100vw"
                  loading="lazy"
                />
              </ImageWrapper>
              <Wrapper>
                <BookingRedeemerCardHeader>
                  <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                    <AccommodationName $variant='body-md-caption'>{booking.description}</AccommodationName>
                    <Location $variant="body-xs">
                      <Icon glyph="MapPin" size={16} />
                      <span>{booking.location}</span>
                    </Location>
                  </SpaceBetween>
                  <BookingPriceDetails>
                    <AmountPaidText $variant='body-xs'>{intl.formatMessage({id: 'checkout_steps_ancillaries_booking_redeemer_paid_text'})}</AmountPaidText>
                    <Typography $variant='body-lg-bold'>{intl.formatCurrency(booking.paidAmount)}</Typography>
                  </BookingPriceDetails>
                </BookingRedeemerCardHeader>
                <BookingRedeemerCardContent>
                  <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
                    <BookingRedeemContentDetails $variant='body-sm'>
                      <Icon glyph="AirplaneToRightTop" size={16} />
                      <span>{booking.date}</span>
                    </BookingRedeemContentDetails>
                    <BookingRedeemContentDetails $variant='body-sm'>
                    <Icon glyph="Family" size={16} />
                      <span>{booking.composition}</span>
                    </BookingRedeemContentDetails>
                    <BookingRedeemContentDetails $variant='body-sm'>
                      <Icon glyph="CheckMarkInCircle" size={16} />
                      <span>{intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_booking_number' })}: {booking.bookingNumber}</span>
                    </BookingRedeemContentDetails>
                  </SpaceBetween>
                  <AddButton $hierarchy="secondary" $size="small" onClick={() => handleClickValue(booking.bookingNumber)}>
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_add_button' })}
                  </AddButton>
                  <RemoveButton $hierarchy="tertiary" $withoutColor $size="small" onClick={() => handleClickValue(null)}>
                    <Icon glyph="Dismiss" size={16} />
                    {intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_remove_button' })}
                  </RemoveButton>
                </BookingRedeemerCardContent>
              </Wrapper>
            </BookingRedeemerCard>
            <BookingRedeemSelectedBanner>
              <Banner $type={'success' as BannerType} >
                <Typography $variant='body-md'>{intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_booking_selected' })}</Typography>
              </Banner>
            </BookingRedeemSelectedBanner>
          </BookingRedeem>
        ))
        }
      </ShowMore>
      { !selectedValue && (
        <Banner $type={'warning' as BannerType} >
          <Typography $variant='body-md'>{intl.formatMessage({ id: 'checkout_steps_ancillaries_booking_redeemer_no_booking_selected' })}</Typography>
        </Banner>
      )}
    </ServiceBox>
  );
}

const BookingRedeemerUsps = styled(BulletList.Root)`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${tk('spacing_md')};

  @media ${tk('mq_desktop_md')} {
    justify-content: space-evenly;
  }

  ${BulletList.Item} {
    white-space: break-spaces;
    display: flex;
    @media ${tk('mq_desktop_md')} {
      max-width: 300px;
    }
  }
  ${Icon} {
    color: ${tk('color_semantic_icon_success')};
  }
`;

const DescriptionTexts = styled.div`
  margin-bottom: ${tk('spacing_md')};
`
const BookingRedeem = styled.div<{ $active?: boolean;}>`
  ${({ $active }) => $active && modActive()}
`
const BookingRedeemerCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 2px solid ${tk('color_semantic_border')};
  border-radius: ${tk('radius_large')};
  margin-bottom: ${tk('spacing_md')};

  :not(:last-child) {
    margin-bottom: ${tk('spacing_none')};
  }

  @media ${tk('mq_desktop_md')} {
    flex-direction: row;
  }
`

const ImageWrapper = styled.div`
  border-top-left-radius: ${tk('radius_large')};
  border-top-right-radius: ${tk('radius_large')};
  width: 100%;
  height: 220px;

  > div {
    border-top-left-radius: ${tk('radius_large')};
    border-top-right-radius: ${tk('radius_large')};
  }

  @media ${tk('mq_desktop_md')} {
    border-top-left-radius: ${tk('radius_large')};
    border-bottom-left-radius: ${tk('radius_large')};
    border-top-right-radius: ${tk('radius_none')};
    width: 200px;
    flex: 0 0 200px;
    min-height: 180px;
    height: auto;
    > div {
      border-top-left-radius: ${tk('radius_large')};
      border-bottom-left-radius: ${tk('radius_large')};
      border-top-right-radius: ${tk('radius_none')};
    }
  }
`
const BookingRedeemerCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
`
const AddButton = styled(Button)``
const RemoveButton = styled(Button)`
  display: none;
`
const BookingRedeemerCardContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;

  ${AddButton} {
    margin-top: ${tk('spacing_md')};
  }
  ${RemoveButton} {
    margin-top: ${tk('spacing_md')};
  }
  @media ${tk('mq_desktop_md')} {
    justify-content: space-between;
    flex-direction: row;
    align-items: flex-end;
  }
`
const BookingPriceDetails = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column-reverse;
  align-items: flex-end;
  @media ${tk('mq_desktop_md')} {
    flex-direction: column;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${tk('spacing_lg')};
  gap: ${tk('spacing_md')};
  width: 100%;
`
const Location = styled(Typography)`
  align-items: center;
  color: ${tk('color_semantic_text_strong')};
  display: flex;
  margin-top: ${tk('spacing_xs')};

  & > *:first-child {
    flex-shrink: 0;
    margin-right: ${tk('spacing_2xs')};
  }
`;

const BookingRedeemContentDetails = styled(Typography)`
  align-items: center;
  display: flex;
  color: ${tk('color_semantic_text')};
  & > *:first-child {
    flex-shrink: 0;
    margin-right: ${tk('spacing_2xs')};
  }
`
const AmountPaidText = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
`
const AccommodationName = styled(Typography)`
  color: ${tk('color_semantic_text')};
`
const BookingRedeemSelectedBanner = styled.div`
  display: none;
  margin-bottom: ${tk('spacing_md')};
`

const modActive = () => css`
  ${BookingRedeemerCard} {
    border-color: ${tk('color_semantic_border_active')};
    ${AddButton} {
      display: none;
    }
    ${RemoveButton} {
      display: flex;
    }
  }

  ${BookingRedeemSelectedBanner} {
    display: block;
    ${Icon} {
      display: none;
    }
  }


`;
